import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Stack } from '@mui/material'
import {
	getVendorStaffEvents,
	updateVendorTask,
} from '../../../features/schedulerSlice.js'
import { formatCommonDate } from '../../../util/formatDateAndTime.js'
import { TASK_PRIORITY, TASK_STATUS_OPTIONS } from '../../../util/constants.js'
import SelectCustom from '../../atoms/select/SelectCustom.jsx'
import { toastHelper } from '../../../util/toastHelper.js'
import { exportCsv } from '../../../util/index.js'

const availableStatuses = TASK_STATUS_OPTIONS.filter(
	(option) => option.value !== 'Completed'
).map((option) => option.value)

export default function useVendorStaffTasks() {
	const dispatch = useDispatch()
	const events = useSelector((s) => s.scheduler.timelineEvents)
	const user = useSelector((state) => state.auth.user)

	const [search, setSearch] = useState('')
	const [statusFilter, setStatusFilter] = useState(availableStatuses)
	const [priorityFilter, setPriorityFilter] = useState([])
	const [innerTab, setInnerTab] = useState(0)
	const [filterAnchor, setFilterAnchor] = useState(null)

	useEffect(() => {
		dispatch(getVendorStaffEvents())
	}, [dispatch])

	const filteredRows = useMemo(() => {
		if (!events?.length) return []
		const q = search.trim().toLowerCase()

		return events.filter((r) => {
			if (q && !r.Subject.toLowerCase().includes(q)) return false
			if (statusFilter.length && !statusFilter.includes(r.status))
				return false
			if (priorityFilter.length && !priorityFilter.includes(r.priority))
				return false
			return true
		})
	}, [search, statusFilter, priorityFilter, events])

	const handleStatusUpdate = (rowId, status) => {
		dispatch(updateVendorTask({ id: rowId, data: { status } }))
	}

	const chips = useMemo(() => {
		const result = []

		statusFilter.forEach((s) => {
			result.push({
				label: `Status: ${s}`,
				onDelete: () =>
					setStatusFilter((prev) => prev.filter((x) => x !== s)),
			})
		})

		priorityFilter.forEach((p) => {
			const priorityLabel = TASK_PRIORITY[p]?.label
			result.push({
				label: `Priority: ${priorityLabel}`,
				onDelete: () =>
					setPriorityFilter((prev) => prev.filter((x) => x !== p)),
			})
		})

		return result
	}, [statusFilter, priorityFilter])

	const columns = useMemo(
		() => [
			{
				field: 'Subject',
				headerName: 'Task',
				flex: 1,
				renderCell: (params) => (
					<span className="role-text">{params.value || '--'}</span>
				),
			},
			{
				field: 'EndTime',
				headerName: 'Due Date',
				flex: 1,
				renderCell: (params) => (
					<span>{formatCommonDate(params.value)}</span>
				),
			},
			{
				field: 'priority',
				headerName: 'Priority',
				flex: 1,
				renderCell: ({ value }) => (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box
							component="span"
							sx={{ fontWeight: 600 }}
							className="priority-icon"
						>
							<img
								src={
									value === 0
										? '/images/high-prio-icon.svg'
										: value === 1
											? '/images/med-prio-icon.svg'
											: '/images/low-prio-icon.svg'
								}
								alt="priority icon"
							/>
							{TASK_PRIORITY[value]?.label || '--'}
						</Box>
					</Stack>
				),
			},
			{
				field: 'Description',
				headerName: 'Comments',
				flex: 1,
				renderCell: (params) => params.value || '--',
			},
			{
				field: 'status',
				headerName: 'Status',
				flex: 1,
				renderCell: (params) => (
					<SelectCustom
						defaultValue={params.value}
						options={TASK_STATUS_OPTIONS}
						setDataFunction={({ value }) =>
							handleStatusUpdate(params.row._id, value)
						}
						renderValueAsLabel={true}
					/>
				),
			},
		],
		[]
	)

	const handleExport = () => {
		const header = ['Task', 'Due Date', 'Priority', 'Comments', 'Status']
		const rows = filteredRows.map((row) => [
			row.Subject || '--',
			formatCommonDate(row.EndTime),
			TASK_PRIORITY[row.priority]?.label || '--',
			row.Description || '--',
			row.status || '--',
		])
		exportCsv([header, ...rows], 'vendor-staff')
	}

	const handleInnerTab = (e, tab) => setInnerTab(tab)

	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)
	const clearFilters = () => {
		setStatusFilter(availableStatuses)
		setPriorityFilter([])
	}

	const partyMembers = [
		{ Id: user._id, OwnerText: user.businessName || user.firstName },
	]

	const onActionBegin = async (args) => {
		if (
			args.requestType === 'eventCreate' ||
			args.requestType === 'eventChange' ||
			args.requestType === 'eventRemove'
		) {
			return toastHelper(
				'You only have view permissions as a vendor FOR NOW',
				'error'
			)
		}
	}

	const eventSettings = useMemo(
		() => ({
			allowAdding: false,
			allowEditing: false,
			allowDeleting: false,
			allowResizing: false,
			allowDrag: false,
			dataSource: events.map((event) => ({
				...event,
			})),
		}),
		[events]
	)

	return {
		events,
		search,
		setSearch,
		statusFilter,
		setStatusFilter,
		priorityFilter,
		setPriorityFilter,
		innerTab,
		setInnerTab,
		filteredRows,
		columns,
		filterAnchor,
		setFilterAnchor,
		chips,
		handleExport,
		openFilter,
		closeFilter,
		clearFilters,
		handleInnerTab,
		partyMembers,
		onActionBegin,
		eventSettings,
	}
}
